<script setup lang="ts">
import type { Colormap } from "@/types";
import { watch, ref, computed, onMounted } from "vue";
import { useStyleStore } from "@/store";

const styleStore = useStyleStore();

const canvas = ref();
const tooltipActivator = ref();
const hoverXProportion = ref();

const emit = defineEmits(["hover"]);
const props = defineProps<{
  colormap: Colormap;
  discrete: boolean;
  proportional?: boolean;
  tooltip?: boolean;
  nColors: number;
  range?: [number | undefined, number | undefined];
}>();

const hoverMarker = computed(() => {
  if (!hoverXProportion.value) return undefined;
  return props.colormap.markers?.find(
    (marker) => hoverXProportion.value < marker.value,
  );
});

function draw() {
  let markers = props.colormap.markers;
  if (!markers) return;
  const ctx = canvas.value.getContext("2d");
  const rect = [0, 0, canvas.value.width, canvas.value.height];
  ctx.clearRect(...rect);
  if (props.discrete) {
    if (props.nColors > 0 && !props.proportional) {
      markers = styleStore.colormapMarkersSubsample(
        {
          id: -1,
          name: "colormap",
          markers,
          project: null,
        },
        {
          discrete: props.discrete,
          n_colors: props.nColors,
        },
      );
    }
    if (markers)
      markers.forEach((marker, index) => {
        if (markers) {
          ctx.fillStyle = marker.color;
          let start = (canvas.value.width / markers.length) * index;
          let end = (canvas.value.width / markers.length) * (index + 1);
          if (props.proportional) {
            start =
              (index > 0 ? markers[index - 1].value : 0) * canvas.value.width;
            end = marker.value * canvas.value.width;
          }
          ctx.fillRect(start, 0, end - start, canvas.value.height);
        }
      });
  } else {
    const gradient = ctx.createLinearGradient(0, 0, canvas.value.width, 0);
    markers.forEach((marker) => {
      gradient.addColorStop(marker.value, marker.color);
    });
    ctx.fillStyle = gradient;
    ctx.fillRect(...rect);
  }
}
onMounted(draw);
watch(
  [
    () => props.colormap,
    () => props.discrete,
    () => props.nColors,
    () => props.colormap.markers,
  ],
  draw,
  { deep: true },
);
watch(hoverMarker, () => {
  emit("hover", hoverMarker.value);
});
</script>

<template>
  <div
    ref="tooltipActivator"
    style="width: 100%"
    @mousemove="(e) => (hoverXProportion = e.offsetX / canvas.clientWidth)"
    @mouseleave="(e) => (hoverXProportion = undefined)"
  >
    <span v-if="props.range">{{ props.range[0]?.toPrecision(3) }}</span>
    <canvas ref="canvas" class="colormap-canvas"></canvas>
    <v-tooltip v-if="tooltip" :activator="tooltipActivator">
      {{ hoverMarker?.label }}
    </v-tooltip>
    <span v-if="props.range">{{ props.range[1]?.toPrecision(3) }}</span>
  </div>
</template>

<style scoped>
.colormap-canvas {
  border: 1px solid rgb(var(--v-theme-on-surface-variant));
  height: 20px;
  width: 100%;
}
</style>
